<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">

    <div class="row no-gutter h-100">
      <div class="col-3" v-if="showList">
        <div v-if="showDateFilter">
          <ReportFilterPopover
            headerTitle="DIFOT Filter"
            :showApplyButton="true"
            :showResetButton="true"
            :appendToBody="true"
            alignment="left"
            placement="bottom-end"
            @apply="$emit('dateApplied')"
            @reset="$emit('dateReset')">
            <template v-slot:popover>
              <ReportDateRangeSelect 
                :startDate="editingStartDate"
                :endDate="editingEndDate"
                @updateStartDate="editingStartDate = $event; $emit('updateEditingStartDate', $event)"
                @updateEndDate="editingEndDate = $event; $emit('updateEditingEndDate', $event)"
              />
            </template>
            <template v-slot:filterDisplay>
              <div>
                <i class="far fa-calendar mr-2"></i>{{startDate | formatDate}} - {{endDate | formatDate}}
              </div>                
            </template>
          </ReportFilterPopover>
        </div>
        <StaticDataTable class="static-table-list" v-if="hasChartData" :smallRows="true">
          <template v-slot:header>
            <tr>
              <th width="60%">{{difotResultTableData.nameTitle}}</th>
              <th width="40%">{{difotResultTableData.valueTitle}}</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(dataValue, index) of difotResultTableData.data" :key="index">
              <td>{{dataValue.name}}</td>
              <td><span v-html="dataValue.displayValue" /></td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
      <div :class="showList ? 'col-9' : 'col-12'">
        <ChartDoughnut
          :series="difotResultSeries"
          :legend="difotResultLegend"
          :options="difotResultOptions"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import AnalyticsDateMixin from '@/modules/intelligence/mixins/analyticsDate'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import ChartDoughnut from '@/components/charts/chartDoughnut'

import { ChartDateRangeTypes } from '@/modules/intelligence/types'
import { OperationalReportingResultTypes, ItemOperationalReportingCodeLookup } from '@/store/types'

const DIFOTResultChartTypes = {
  internalCauseMissList: 'internalCauseMiss',
  successHitList: 'successHitList',
  excludeList: 'excludeList',
  fullLatenessList: 'fullLatenessList'
}

export default {
  name: 'OrderDIFOTResultsChartWidget',
  mixins: [AnalyticsDateMixin],
  components: {
    AnalyticsWidget,
    ChartDoughnut,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    stockId: Number,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    showList: Boolean,
    showDateFilter: Boolean,
    dateRangeType: {
      type: String,
      default: ChartDateRangeTypes.today
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    startDate: String,
    endDate: String
  },
  DIFOTResultChartTypes: DIFOTResultChartTypes,
  data() {
    return {
      difotResultSeries: [],
      difotResultTableData: {},
      difotResultLegend: [],
      difotResultOptions: {},

      editingStartDate: this.startDate,
      editingEndDate: this.endDate
    }
  },
  computed: {
    hasChartData() {
      if(this.difotResultSeries) {
        return this.difotResultSeries.some(series => {
          if(series.data && series.data.length > 0)
            return true
        })
      }
      return false
    },
    hasContactFilter() {
      return this.contactId != null
    },
  },
  mounted() {
    this.requestChartData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    },
    dateRangeType: function() {
      this.requestChartData()
    },
    startDate: function() {
      this.editingStartDate = this.startDate
      this.requestChartData()
    },
    endDate: function() {
      this.editingEndDate = this.endDate
      this.requestChartData()
    }
  },
  methods: {
    requestChartData() {
      var summaryFilter = {
        startDate: this.calculateStartDateForDateRangeType(this.dateRangeType, this.startDate).toDate().toISOString(),
        endDate: this.calculateEndDateForDateRangeType(this.dateRangeType, this.endDate).toDate().toISOString(),
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      const widgetComp = this
      this.$apiService.analyticsService.getOrderDIFOTSummary(summaryFilter)
      .then(response => {
        this.difotResultOptions.tooltip = {
          formatter(param) {
            return `${param.seriesName} <br />${param.name} : ${param.value} (${param.percent}%)`
          }
        }

        let codeList = response.data.operatingCodeList

        switch(this.type) {
          case DIFOTResultChartTypes.internalCauseMissList:
            codeList = codeList.filter(codeInfo => codeInfo.resultType == OperationalReportingResultTypes.miss)
            break
          case DIFOTResultChartTypes.successHitList:
            codeList = codeList.filter(codeInfo => codeInfo.resultType == OperationalReportingResultTypes.hit)
            break
          case DIFOTResultChartTypes.excludeList:
            codeList = codeList.filter(codeInfo => codeInfo.resultType == OperationalReportingResultTypes.exclude)
            break
          case DIFOTResultChartTypes.fullLatenessList:
            codeList = codeList.filter(codeInfo => codeInfo.resultType == OperationalReportingResultTypes.miss || codeInfo.resultType == OperationalReportingResultTypes.exclude)
            break
        }

        this.difotResultSeries = [{
          name: 'Code',
          data: codeList.map(codeInfo => {
            return {
              name: this.formatLookupName(codeInfo.code, ItemOperationalReportingCodeLookup),
              value: codeInfo.totalOrderCount
            }
          }),
          label: {
            show: true,
            formatter(param) {
              return param.name
            }
          },
        }]
        this.difotResultLegend = {
          data: codeList.map(codeInfo => this.formatLookupName(codeInfo.code, ItemOperationalReportingCodeLookup))
        }

        this.difotResultTableData = {
          nameTitle: 'Reason',
          valueTitle: 'Orders',
          data: codeList.map(codeInfo => {
            return {
              name: this.formatLookupName(codeInfo.code, ItemOperationalReportingCodeLookup),
              displayValue: codeInfo.totalOrderCount
            }
          })
        }

        this.difotResultLegend.show = this.showLegend
      })
    },
  }
}
</script>